import { computed, ref, type ComputedRef, type Ref } from 'vue';

import type { HubTextSize } from '@apexx/auth';

export type HubTextSizeDraftSession = {
	draft: Ref<HubTextSize>;
	baseline: Ref<HubTextSize>;
	isDirty: ComputedRef<boolean>;
	select: (size: HubTextSize) => void;
	revert: () => void;
	markSaved: () => void;
};

/**
 * Draft/baseline session for Settings text-size Save/Cancel.
 * Preview calls `apply`; Preferences/cloud persistence stays outside this helper.
 */
export function createHubTextSizeDraftSession(
	initialBaseline: HubTextSize,
	apply: (size: HubTextSize) => void,
): HubTextSizeDraftSession {
	const baseline = ref<HubTextSize>(initialBaseline);
	const draft = ref<HubTextSize>(initialBaseline);
	const isDirty = computed(() => draft.value !== baseline.value);

	function select(size: HubTextSize): void {
		draft.value = size;
		apply(size);
	}

	function revert(): void {
		if (draft.value === baseline.value) {
			return;
		}

		draft.value = baseline.value;
		apply(baseline.value);
	}

	function markSaved(): void {
		baseline.value = draft.value;
	}

	return {
		draft,
		baseline,
		isDirty,
		select,
		revert,
		markSaved,
	};
}
